/**
 * Derived from flubber@0.4.2 (MIT).
 * Adapted for dependency-free internal ESM use in anvil2.
 */
import { pathStringToRing } from "./svg";
import { bisect } from "./add";
import { INVALID_INPUT } from "./errors";
import {
  isFiniteNumber,
  polygonArea,
  samePoint,
  type Ring,
  type RingPoint,
} from "./math";

type NormalizedRingInput = string | Ring;
type PathConversionResult = {
  ring: Ring;
  skipBisect?: boolean;
};

export function normalizeRing(
  ring: NormalizedRingInput,
  maxSegmentLength?: number,
) {
  let skipBisect = false;

  if (typeof ring === "string") {
    const converted: PathConversionResult = pathStringToRing(
      ring,
      maxSegmentLength,
    );
    ring = converted.ring;
    skipBisect = converted.skipBisect ?? false;
  } else if (!Array.isArray(ring)) {
    throw new TypeError(INVALID_INPUT);
  }

  const points = ring.slice(0) as Ring;

  if (!validRing(points)) {
    throw new TypeError(INVALID_INPUT);
  }

  if (points.length > 1 && samePoint(points[0], points[points.length - 1])) {
    points.pop();
  }

  if (polygonArea(points) > 0) {
    points.reverse();
  }

  if (
    !skipBisect &&
    maxSegmentLength &&
    isFiniteNumber(maxSegmentLength) &&
    maxSegmentLength > 0
  ) {
    bisect(points, maxSegmentLength);
  }

  return points;
}

function validRing(ring: Ring) {
  return ring.every(
    (point: RingPoint) =>
      Array.isArray(point) &&
      point.length >= 2 &&
      isFiniteNumber(point[0]) &&
      isFiniteNumber(point[1]),
  );
}
